// Api
import API from '@/api'
// Services
import { http } from '@/boot/axios'

/**
 * Modulo de licencias del Super Admin.
 *
 * Todo pega contra binnacle-graphql-api (API.GRAPHQL_URL). Nada usa binnacle-api.
 *
 * Son endpoints dedicados bajo `licenses/`, no los genericos de `organizations` y `users`: los
 * conteos de licencias se cobrarian a todos los consumidores actuales de esos endpoints.
 *
 * La paginacion de las tres lecturas viene anidada en `meta`, no aplanada en la raiz.
 * Ver backend-licencias.md.
 */
const BASE_URL = `${API.GRAPHQL_URL}licenses/`

export default {
  /**
   * Lecturas. Las tres ordenan en servidor con `sort_by` y `sort_dir`, validados contra una lista
   * blanca por endpoint: una llave fuera de esa lista responde 422 en vez de ordenar por algo
   * arbitrario. Sin ninguno de los dos, el backend ordena por `id` ascendente.
   *
   * `sortBy` nulo (la tabla con el orden quitado) viaja omitido, no vacio: axios descarta los
   * params nulos y el backend cae en su propio default.
   */
  fetchOrganizations ({ page, limit, search, sortBy, sortDir }) {
    return http.get(`${BASE_URL}organizations`, {
      params: { page, limit, search, sort_by: sortBy, sort_dir: sortDir }
    })
  },

  // Devuelve tambien un bloque `organization` para el encabezado con deep-link directo.
  fetchFranchises ({ organizationId, page, limit, search, sortBy, sortDir }) {
    return http.get(`${BASE_URL}organizations/${organizationId}/franchises`, {
      params: { page, limit, search, sort_by: sortBy, sort_dir: sortDir }
    })
  },

  // `status` acepta 1, 0, o ausente para todos. Axios omite los params nulos.
  //
  // `licenseType`/`assignedFrom`/`assignedTo` son para la vista previa del Reporte de Licencias
  // (TKT-2285): el endpoint de listado no los documenta todavia (el backend del reporte los valida
  // solo en `reports/licensesExcelEmail`, ver licenseReport.js), pero viajan igual porque Axios
  // omite los nulos -- no rompen a los tres consumidores existentes (Organizations/Franchises/Users
  // pages, que nunca los pasan) y quedan listos para filtrar de verdad en cuanto el listado los
  // soporte, sin otro cambio de frontend.
  fetchUsers ({ organizationId, franchiseId, status, search, page, limit, sortBy, sortDir, licenseType, assignedFrom, assignedTo }) {
    return http.get(`${BASE_URL}users`, {
      params: {
        organization_id: organizationId,
        franchise_id: franchiseId,
        status,
        search,
        page,
        limit,
        sort_by: sortBy,
        sort_dir: sortDir,
        license_type: licenseType,
        assigned_from: assignedFrom,
        assigned_to: assignedTo
      }
    })
  },

  // KPI del Reporte de Licencias, fase 2 (TKT-2285). No paginado -- un solo objeto agregado por
  // alcance. Contrato documentado por el companion backend feat/TKT-2285-kpi-dashboard-backend
  // (en construccion en paralelo, no verificado contra una instancia real): a reconciliar en
  // revision si el backend real difiere en nombres de campo.
  fetchSummary ({ organizationId, franchiseId }) {
    return http.get(`${BASE_URL}summary`, {
      params: {
        organization_id: organizationId,
        franchise_id: franchiseId
      }
    })
  },

  // KPI global de la plataforma, fase 3 (TKT-2285). Sin alcance (no recibe organization_id ni
  // franchise_id): son los totales de TODAS las organizaciones, cacheados en Redis del lado del
  // backend. Contrato documentado por el companion backend feat/TKT-2285-fase3-kpi-global-pdf (en
  // construccion en paralelo, no verificado contra una instancia real): a reconciliar en revision
  // si el backend real difiere en nombres de campo. Misma forma de respuesta que `sync` de abajo,
  // mas `cached_at`.
  fetchGlobalSummary () {
    return http.get(`${BASE_URL}summary/global`)
  },

  // Fuerza un recalculo del KPI global en el backend (botón "Sincronizar"), en vez de servir el
  // valor cacheado. Responde el mismo shape que `fetchGlobalSummary`.
  syncGlobalSummary () {
    return http.post(`${BASE_URL}summary/global/sync`)
  },

  /**
   * Escrituras. Responden `{ affected }` con los cambios reales, no con las filas del alcance.
   * Tope de 1000 ids por peticion en las tres.
   *
   * `reasonCode` es OBLIGATORIO en las tres: sin el, el backend responde 422. El catalogo valido
   * depende del `status` del mismo request (suspension y reactivacion tienen listas distintas), y
   * `reasonDetail` solo es obligatorio cuando la razon es `other`. Quien arma ambos valores es
   * LicenseStatusReasonDialog, via el composable licenseBulkActions.
   *
   * `setUsersStatus` puede responder 422 si el lote nombra a personal de Binnacle (roles 3 o 12):
   * rechaza la peticion completa y devuelve `protected_user_ids`. Lo maneja el composable
   * licenseBulkActions.
   */
  setUsersStatus ({ userIds, status, reasonCode, reasonDetail }) {
    return http.patch(`${BASE_URL}users/status`, {
      user_ids: userIds,
      status,
      reason_code: reasonCode,
      reason_detail: reasonDetail
    })
  },
  setFranchisesStatus ({ franchiseIds, status, reasonCode, reasonDetail }) {
    return http.patch(`${BASE_URL}franchises/status`, {
      franchise_ids: franchiseIds,
      status,
      reason_code: reasonCode,
      reason_detail: reasonDetail
    })
  },
  setOrganizationsStatus ({ organizationIds, status, reasonCode, reasonDetail }) {
    return http.patch(`${BASE_URL}organizations/status`, {
      organization_ids: organizationIds,
      status,
      reason_code: reasonCode,
      reason_detail: reasonDetail
    })
  }
}
